import { memo, useCallback, useMemo, useState } from "react";
import { Category } from "@common/types/category";
import { BreakableText } from "@design-system/BreakableText/BreakableText";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { Picker } from "@design-system/Picker/Picker.lib";
import { useCategoriesById } from "@hooks/useCategoriesById";
import { CategoryCombobox } from "../../transactions/TransactionSplitV2/CategoryCombobox";
import { CategoryIcon } from "../../transactions/CategoryIcon";
import { useSimilarTransactionsNewValue } from "./SimilarTransactionsNewValueContext";
import styles from "./CategoryNewValueCell.module.scss";

export const CategoryNewValueCell: React.FC<{
    transactionId: number;
    currentValue: string;
}> = memo(({ transactionId, currentValue }) => {
    const { setNewValue } = useSimilarTransactionsNewValue();
    const categoriesById = useCategoriesById();
    const [isOpen, setIsOpen] = useState(false);

    const categoryId = currentValue;
    const category = categoriesById[categoryId];

    const handleCategoryChange = useCallback(
        (selected: Category | undefined) => {
            if (!selected) {
                return;
            }
            setNewValue(transactionId, selected.id);
            setIsOpen(false);
        },
        [transactionId, setNewValue],
    );

    const triggerContent = useMemo(
        () => (
            <span className={styles.categoryTrigger}>
                {categoryId && <CategoryIcon categoryId={categoryId} />}
                <BreakableText
                    text={category?.label}
                    className={styles.categoryLabel}
                    showTooltip
                />
            </span>
        ),
        [categoryId, category?.label],
    );

    const trigger = useMemo(
        () => (
            <Picker.CellContent
                value={triggerContent}
                aria-label={category?.label}
            />
        ),
        [triggerContent, category?.label],
    );

    return (
        <ViewTableCell captureClicks>
            <CategoryCombobox
                selectedCategory={category}
                onChange={handleCategoryChange}
                suggestedCategories={[]}
                hideArrow
                open={isOpen}
                onOpenChange={setIsOpen}
                trigger={trigger}
            />
        </ViewTableCell>
    );
});
CategoryNewValueCell.displayName = "CategoryNewValueCell";
